import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface TemplateConfigDialogData {
  // The current Value 2 of the step (a JSON template string), possibly empty.
  template: string;
}

type RuleType =
  | 'exact'
  | 'not_null'
  | 'not_empty'
  | 'contains'
  | 'numeric'
  | 'variable'
  | 'type'
  | 'array_size'
  | 'length'
  | 'no_special_chars'
  | 'array_object';

const MAX_LOOP_RANGE = 100;

type Operator = '>' | '<' | '>=' | '<=' | '=';

type Severity = 'assert' | 'verify' | 'ignore';

interface TemplateRow {
  path: string;
  ruleType: RuleType;
  operator?: Operator;
  param?: string;
  severity: Severity;
  loopStart?: string;
  loopEnd?: string;
  subTemplate?: string;
  groupId: number;
}

interface FieldGroup {
  groupId: number;
  rows: TemplateRow[];
}

interface SubTemplateEntry {
  ruleLabel: string;
  detail: string;
  severity: Severity;
}

interface SubTemplateFieldGroup {
  path: string;
  entries: SubTemplateEntry[];
}

interface SubContext {
  fieldPath: string;
  rangeLabel: string;
}

interface StackFrame {
  jsonText: string;
  rows: TemplateRow[];
  viewMode: ViewMode;
  nextGroupId: number;
  parentSubContext: SubContext | null;
  editingRow: TemplateRow;
}

type ViewMode = 'json' | 'table';

const RULE_TYPES: { value: RuleType; label: string }[] = [
  { value: 'exact', label: 'Equals to' },
  { value: 'not_null', label: 'Not null' },
  { value: 'not_empty', label: 'Not empty (array)' },
  { value: 'array_size', label: 'Array size' },
  { value: 'length', label: 'String length' },
  { value: 'no_special_chars', label: 'No special characters' },
  { value: 'array_object', label: 'Array object (loop check)' },
  { value: 'contains', label: 'Contains' },
  { value: 'numeric', label: 'Numeric comparison' },
  { value: 'variable', label: 'Compare to variable (##)' },
  { value: 'type', label: 'Data type' },
];

const NUMERIC_OPERATORS: Operator[] = ['>', '<', '>=', '<='];
const SIZE_LENGTH_OPERATORS: Operator[] = ['>', '<', '>=', '<=', '='];

const SEVERITIES: { value: Severity; label: string }[] = [
  { value: 'assert', label: 'Assert' },
  { value: 'verify', label: 'Verify' },
  { value: 'ignore', label: 'Ignore' },
];

const DATA_TYPES: string[] = ['string', 'number', 'boolean', 'array', 'object', 'null'];

@Component({
  selector: 'app-template-config-dialog',
  templateUrl: './template-config-dialog.component.html',
  styleUrls: ['./template-config-dialog.component.scss'],
})
export class TemplateConfigDialogComponent implements OnInit {
  viewMode: ViewMode = 'json';
  jsonText = '';
  rows: TemplateRow[] = [];
  jsonError = '';
  ruleTypes = RULE_TYPES;
  numericOperators = NUMERIC_OPERATORS;
  sizeLengthOperators = SIZE_LENGTH_OPERATORS;
  dataTypes = DATA_TYPES;
  severities = SEVERITIES;
  maxLoopRange = MAX_LOOP_RANGE;
  currentSubContext: SubContext | null = null;

  get fullSubPath(): string {
    return this.stack
      .map((frame) => {
        const row = frame.editingRow;
        return (row.path || '(unnamed field)') + '[' + this.effectiveLoopBoundDisplay(row.loopStart, 1) + '-' + this.effectiveLoopBoundDisplay(row.loopEnd, MAX_LOOP_RANGE) + ']';
      })
      .join('.');
  }

  private loopBoundDisplay(stored: string | undefined): number | null {
    if (stored === undefined || stored === null || stored === '') {
      return null;
    }
    const n = Number(stored);
    return isNaN(n) ? null : n + 1;
  }

  private effectiveLoopBoundDisplay(stored: string | undefined, fallbackDisplayed: number): number {
    const displayed = this.loopBoundDisplay(stored);
    return displayed === null ? fallbackDisplayed : displayed;
  }

  private loopBoundStored(displayed: number | null): string | undefined {
    if (displayed === null || displayed === undefined) {
      return undefined;
    }
    const n = Number(displayed);
    return isNaN(n) ? undefined : String(Math.max(0, n - 1));
  }

  loopStartDisplay(row: TemplateRow): number | null {
    return this.loopBoundDisplay(row.loopStart);
  }

  onLoopStartDisplayChange(row: TemplateRow, displayed: number | null): void {
    row.loopStart = this.loopBoundStored(displayed);
  }

  loopEndDisplay(row: TemplateRow): number | null {
    return this.loopBoundDisplay(row.loopEnd);
  }

  onLoopEndDisplayChange(row: TemplateRow, displayed: number | null): void {
    row.loopEnd = this.loopBoundStored(displayed);
  }

  private nextGroupId = 1;
  private stack: StackFrame[] = [];

  get groups(): FieldGroup[] {
    const order: number[] = [];
    const byId = new Map<number, TemplateRow[]>();
    this.rows.forEach((row) => {
      if (!byId.has(row.groupId)) {
        order.push(row.groupId);
        byId.set(row.groupId, []);
      }
      byId.get(row.groupId)!.push(row);
    });
    return order.map((groupId) => ({ groupId, rows: byId.get(groupId)! }));
  }

  trackByGroup(_index: number, group: FieldGroup): number {
    return group.groupId;
  }

  constructor(
    public dialogRef: MatDialogRef<TemplateConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TemplateConfigDialogData
  ) {}

  ngOnInit(): void {
    this.jsonText = this.data && this.data.template ? this.data.template : '{\n\n}';
    this.rebuildRowsFromJson();
  }

  switchToTable(): void {
    if (!this.rebuildRowsFromJson()) {
      return;
    }
    this.viewMode = 'table';
  }

  switchToJson(): void {
    this.jsonText = JSON.stringify(this.buildJsonFromRows(), null, 2);
    this.viewMode = 'json';
  }

  private rebuildRowsFromJson(beforeAction: string = 'switching to Table view'): boolean {
    try {
      const parsed = this.jsonText.trim() === '' ? {} : JSON.parse(this.jsonText);
      this.rows = [];
      this.nextGroupId = 1;
      this.flatten(parsed, '', this.rows);
      this.jsonError = '';
      return true;
    } catch (e) {
      this.jsonError = `Invalid JSON — fix it here before ${beforeAction}.`;
      return false;
    }
  }

  private flatten(value: any, path: string, rows: TemplateRow[]): void {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        rows.push({ path, ruleType: 'exact', param: '[]', severity: 'assert', groupId: this.nextGroupId++ });
        return;
      }
      value.forEach((item, index) => {
        this.flatten(item, path + '[' + index + ']', rows);
      });
      return;
    }
    if (value !== null && typeof value === 'object' && value.__CHECKS__ === true && Array.isArray(value.checks)) {
      const groupId = this.nextGroupId++;
      (value.checks as any[]).forEach((check) => {
        this.pushCheckRow(check, path, rows, groupId);
      });
      return;
    }
    if (value !== null && typeof value === 'object' && value.__ARRAY_LOOP__ === true) {
      // Loop marker leaf row -- must precede the generic object branch or its keys get treated as fields.
      rows.push(this.buildArrayObjectRow(value, path, this.nextGroupId++));
      return;
    }
    if (value !== null && typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        const childPath = path ? path + '.' + key : key;
        this.flatten(value[key], childPath, rows);
      });
      return;
    }
    if (typeof value === 'string') {
      // Self-heals a since-fixed bug where __ARRAY_LOOP__ got wrapped as a VERIFY/ASSERT string; must run before the " && " split below.
      const recoveredLoop = this.tryParseStringifiedArrayLoop(value);
      if (recoveredLoop) {
        rows.push(this.buildArrayObjectRow(recoveredLoop, path, this.nextGroupId++));
        return;
      }
      if (value.indexOf(' && ') !== -1) {
        // Combined directive ("NOT_NULL && LENGTH>=3") -- split into one row per check under the same groupId.
        const groupId = this.nextGroupId++;
        value.split(/\s*&&\s*/).forEach((part) => {
          const trimmed = part.trim();
          if (trimmed !== '') {
            rows.push({ ...this.parseDirective(path, trimmed), groupId });
          }
        });
        return;
      }
    }
    rows.push({ ...this.parseDirective(path, value), groupId: this.nextGroupId++ });
  }

  // One entry from a __CHECKS__ list; shares the caller's groupId instead of allocating a new one.
  private pushCheckRow(value: any, path: string, rows: TemplateRow[], groupId: number): void {
    if (value !== null && typeof value === 'object' && value.__ARRAY_LOOP__ === true) {
      rows.push(this.buildArrayObjectRow(value, path, groupId));
      return;
    }
    if (typeof value === 'string') {
      const recoveredLoop = this.tryParseStringifiedArrayLoop(value);
      if (recoveredLoop) {
        rows.push(this.buildArrayObjectRow(recoveredLoop, path, groupId));
        return;
      }
    }
    rows.push({ ...this.parseDirective(path, value), groupId });
  }

  // Recovers a VERIFY/ASSERT-wrapped __ARRAY_LOOP__ marker; returns null for any other shape.
  private tryParseStringifiedArrayLoop(value: string): any | null {
    let inner: string | null = null;
    if (value.indexOf('VERIFY(') === 0 && value.lastIndexOf(')') === value.length - 1) {
      inner = value.substring('VERIFY('.length, value.length - 1);
    } else if (value.indexOf('ASSERT(') === 0 && value.lastIndexOf(')') === value.length - 1) {
      inner = value.substring('ASSERT('.length, value.length - 1);
    }
    if (inner === null) {
      return null;
    }
    try {
      const parsed = JSON.parse(inner);
      if (parsed !== null && typeof parsed === 'object' && parsed.__ARRAY_LOOP__ === true) {
        return parsed;
      }
    } catch (e) {
      // Not JSON after all -- an ordinary wrapped directive, nothing to recover.
    }
    return null;
  }

  private buildArrayObjectRow(value: any, path: string, groupId: number): TemplateRow {
    return {
      path,
      ruleType: 'array_object',
      loopStart: value.start !== undefined && value.start !== null ? String(value.start) : '0',
      loopEnd: value.end !== undefined && value.end !== null ? String(value.end) : '0',
      subTemplate: JSON.stringify(value.template || {}, null, 2),
      severity: value.verify === true ? 'verify' : 'assert',
      groupId,
    };
  }

  // Peels off an ASSERT/VERIFY wrapper (default 'assert'), then parses what's left; groupId is assigned by the caller.
  private parseDirective(path: string, value: any): Omit<TemplateRow, 'groupId'> {
    if (typeof value !== 'string') {
      return { path, ruleType: 'exact', param: JSON.stringify(value), severity: 'assert' };
    }
    let severity: Severity = 'assert';
    let inner = value;
    if (value.indexOf('VERIFY(') === 0 && value.lastIndexOf(')') === value.length - 1) {
      severity = 'verify';
      inner = value.substring('VERIFY('.length, value.length - 1);
    } else if (value.indexOf('ASSERT(') === 0 && value.lastIndexOf(')') === value.length - 1) {
      severity = 'assert';
      inner = value.substring('ASSERT('.length, value.length - 1);
    }
    return { ...this.parseInnerDirective(path, inner), severity };
  }

  private parseInnerDirective(path: string, value: string): Omit<TemplateRow, 'groupId' | 'severity'> {
    if (value === 'NOT_NULL') {
      return { path, ruleType: 'not_null' };
    }
    if (value === 'NOT_EMPTY') {
      return { path, ruleType: 'not_empty' };
    }
    if (value.indexOf('CONTAINS:') === 0) {
      return { path, ruleType: 'contains', param: value.substring('CONTAINS:'.length) };
    }
    if (value.indexOf('TYPE:') === 0) {
      return { path, ruleType: 'type', param: value.substring('TYPE:'.length) };
    }
    if (value === 'NO_SPECIAL_CHARS') {
      return { path, ruleType: 'no_special_chars' };
    }
    if (value.indexOf('##') === 0) {
      return { path, ruleType: 'variable', param: value.substring(2) };
    }
    const sizeMatch = /^SIZE(>=|<=|>|<|=)(\d+)$/.exec(value);
    if (sizeMatch) {
      return { path, ruleType: 'array_size', operator: sizeMatch[1] as Operator, param: sizeMatch[2] };
    }
    const lengthMatch = /^LENGTH(>=|<=|>|<|=)(\d+)$/.exec(value);
    if (lengthMatch) {
      return { path, ruleType: 'length', operator: lengthMatch[1] as Operator, param: lengthMatch[2] };
    }
    const opMatch = /^(>=|<=|>|<)(-?\d+(?:\.\d+)?)$/.exec(value);
    if (opMatch) {
      return { path, ruleType: 'numeric', operator: opMatch[1] as Operator, param: opMatch[2] };
    }
    return { path, ruleType: 'exact', param: value };
  }

  // ===== rows -> JSON =====

  // Wraps non-blocking checks in VERIFY(...); ASSERT is the bare default so it's never emitted explicitly.
  // "array_object" is exempt -- __ARRAY_LOOP__ must stay a raw JSON object, so its "verify" severity is carried as a flag instead (see rowToInnerValue).
  private rowToValue(row: TemplateRow): any {
    const inner = this.rowToInnerValue(row);
    if (row.ruleType === 'array_object' || row.severity !== 'verify') {
      return inner;
    }
    const innerText = typeof inner === 'string' ? inner : JSON.stringify(inner);
    return 'VERIFY(' + innerText + ')';
  }

  private rowToInnerValue(row: TemplateRow): any {
    switch (row.ruleType) {
      case 'not_null':
        return 'NOT_NULL';
      case 'not_empty':
        return 'NOT_EMPTY';
      case 'contains':
        return 'CONTAINS:' + (row.param || '');
      case 'type':
        return 'TYPE:' + (row.param || 'string');
      case 'no_special_chars':
        return 'NO_SPECIAL_CHARS';
      case 'variable':
        return '##' + (row.param || '');
      case 'array_size':
        return 'SIZE' + (row.operator || '>') + (row.param || '0');
      case 'length':
        return 'LENGTH' + (row.operator || '>') + (row.param || '0');
      case 'numeric':
        return (row.operator || '>') + (row.param || '0');
      case 'array_object': {
        let subTemplate: any = {};
        try {
          subTemplate = row.subTemplate ? JSON.parse(row.subTemplate) : {};
        } catch (e) {
          // Invalid hand-typed JSON -- fall back to an empty template.
          subTemplate = {};
        }
        const { start, end } = this.effectiveLoopBounds(row);
        const loopValue: any = {
          __ARRAY_LOOP__: true,
          start,
          end,
          template: subTemplate,
        };
        // No VERIFY() wrapper for a JSON object -- a "verify" flag on the marker itself signals non-blocking instead; omitted (not false) when blocking.
        if (row.severity === 'verify') {
          loopValue.verify = true;
        }
        return loopValue;
      }
      case 'exact':
      default:
        return this.coerceExactValue(row.param || '');
    }
  }

  private coerceExactValue(raw: string): any {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    if (raw !== '' && !isNaN(Number(raw))) return Number(raw);
    // Lets a hand-typed "[]"/"{...}" round-trip as real JSON instead of a literal string.
    const trimmed = raw.trim();
    if (trimmed.length > 0 && (trimmed[0] === '[' || trimmed[0] === '{')) {
      try {
        return JSON.parse(trimmed);
      } catch (e) {
        // not valid JSON after all -- fall through and keep it as a string
      }
    }
    return raw;
  }

  // Splits "results[0].name.first" into ["results", 0, "name", "first"], inverting flatten()'s path building.
  private tokenizePath(path: string): Array<string | number> {
    const tokens: Array<string | number> = [];
    const regex = /([^.[\]]+)|\[(\d+)\]/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(path)) !== null) {
      if (match[1] !== undefined) {
        tokens.push(match[1]);
      } else {
        tokens.push(parseInt(match[2], 10));
      }
    }
    return tokens;
  }

  private buildJsonFromRows(): any {
    // A blank-path "Array object" row means the loop check applies to the whole value -- the root IS the __ARRAY_LOOP__ marker, taking over the entire template.
    const rootArrayObjectRow = this.rows.find(
      (row) => row.ruleType === 'array_object' && !row.path && row.severity !== 'ignore'
    );
    if (rootArrayObjectRow) {
      return this.rowToValue(rootArrayObjectRow);
    }

    const root: any = {};
    // Group by path first so multiple rows on one field combine into one " && " string instead of overwriting.
    const grouped = new Map<string, any[]>();
    this.rows.forEach((row) => {
      if (row.severity === 'ignore' || !row.path) {
        return;
      }
      const values = grouped.get(row.path) || [];
      values.push(this.rowToValue(row));
      grouped.set(row.path, values);
    });
    grouped.forEach((values, path) => {
      const tokens = this.tokenizePath(path);
      if (tokens.length === 0) {
        return;
      }
      let node: any = root;
      for (let i = 0; i < tokens.length - 1; i++) {
        const token = tokens[i];
        const nextIsIndex = typeof tokens[i + 1] === 'number';
        const existing = node[token as any];
        if (nextIsIndex ? !Array.isArray(existing) : (typeof existing !== 'object' || existing === null || Array.isArray(existing))) {
          node[token as any] = nextIsIndex ? [] : {};
        }
        node = node[token as any];
      }
      // A single check keeps its own JSON type; combining several normally joins into " && ", but a loop-check entry needs the __CHECKS__ wrapper instead.
      const hasLoopValue = values.some((v) => v && typeof v === 'object' && (v as any).__ARRAY_LOOP__ === true);
      const finalValue = values.length === 1
        ? values[0]
        : hasLoopValue
          ? { __CHECKS__: true, checks: values }
          : values.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(' && ');
      node[tokens[tokens.length - 1] as any] = finalValue;
    });
    return root;
  }

  // ===== Row / group editing (Table view) =====

  // Propagates the card's single path input to every check in the group.
  onGroupPathChange(group: FieldGroup, newPath: string): void {
    group.rows.forEach((row) => (row.path = newPath));
  }

  // Removing the last check in a group removes the group itself.
  removeCheck(row: TemplateRow): void {
    const index = this.rows.indexOf(row);
    if (index !== -1) {
      this.rows.splice(index, 1);
    }
  }

  // New checks default to Assert, except inside an "Array object" sub-template where they inherit the parent's own severity (see onSeverityChange).
  private defaultSeverityForNewRow(): Severity {
    if (this.stack.length === 0) {
      return 'assert';
    }
    return this.stack[this.stack.length - 1].editingRow.severity === 'verify' ? 'verify' : 'assert';
  }

  // Adds a brand-new field/group with one blank check.
  addRow(): void {
    this.rows.push({ path: '', ruleType: 'not_null', severity: this.defaultSeverityForNewRow(), groupId: this.nextGroupId++ });
  }

  // Adds another check to the group, pre-filled with its path; combines into " && " on save.
  addCheckToGroup(group: FieldGroup): void {
    const lastRow = group.rows[group.rows.length - 1];
    const newRow: TemplateRow = { path: lastRow.path, ruleType: 'not_null', severity: this.defaultSeverityForNewRow(), groupId: group.groupId };
    const insertAt = this.rows.indexOf(lastRow) + 1;
    this.rows.splice(insertAt, 0, newRow);
  }

  // Changing an Array-object row's severity cascades to reset every check in its sub-template (recursively); switching to Ignore leaves children unchanged.
  onSeverityChange(row: TemplateRow, severity: Severity): void {
    row.severity = severity;
    if (row.ruleType === 'array_object' && severity !== 'ignore') {
      this.cascadeSeverityToSubTemplate(row, severity);
    }
  }

  private cascadeSeverityToSubTemplate(row: TemplateRow, severity: 'assert' | 'verify'): void {
    if (!row.subTemplate) {
      return;
    }
    let parsed: any;
    try {
      parsed = JSON.parse(row.subTemplate);
    } catch (e) {
      return; // invalid JSON typed by hand into the nested dialog -- nothing to cascade into
    }
    const savedRows = this.rows;
    const savedNextGroupId = this.nextGroupId;
    this.rows = [];
    this.nextGroupId = 1;
    this.flatten(parsed, '', this.rows);
    this.rows.forEach((r) => {
      if (r.severity !== 'ignore') {
        r.severity = severity;
      }
      if (r.ruleType === 'array_object') {
        this.cascadeSeverityToSubTemplate(r, severity);
      }
    });
    row.subTemplate = JSON.stringify(this.buildJsonFromRows(), null, 2);
    this.rows = savedRows;
    this.nextGroupId = savedNextGroupId;
  }

  // ===== Array object (loop check) =====

  // Read-only preview of the sub-template's checks, reusing flatten() and restoring nextGroupId afterward so this call doesn't burn real group ids.
  getSubTemplateSummary(row: TemplateRow): SubTemplateFieldGroup[] {
    if (row.ruleType !== 'array_object') {
      return [];
    }
    try {
      const parsed = row.subTemplate ? JSON.parse(row.subTemplate) : {};
      const previewRows: TemplateRow[] = [];
      const savedNextGroupId = this.nextGroupId;
      this.flatten(parsed, '', previewRows);
      this.nextGroupId = savedNextGroupId;
      const order: string[] = [];
      const byPath = new Map<string, SubTemplateEntry[]>();
      previewRows.forEach((r) => {
        const entry: SubTemplateEntry = {
          ruleLabel: (this.ruleTypes.find((rt) => rt.value === r.ruleType) || { label: r.ruleType }).label,
          // A nested array-object row shows only its own range here -- its fields require stepping in via "Configure Object Fields".
          detail: r.ruleType === 'numeric' || r.ruleType === 'array_size' || r.ruleType === 'length'
            ? (r.operator || '') + (r.param || '')
            : r.ruleType === 'array_object'
              ? '[' + this.effectiveLoopBoundDisplay(r.loopStart, 1) + '-' + this.effectiveLoopBoundDisplay(r.loopEnd, MAX_LOOP_RANGE) + ']'
              : (r.param || ''),
          severity: r.severity,
        };
        if (!byPath.has(r.path)) {
          order.push(r.path);
          byPath.set(r.path, []);
        }
        byPath.get(r.path)!.push(entry);
      });
      return order.map((path) => ({ path, entries: byPath.get(path)! }));
    } catch (e) {
      return [{ path: '', entries: [{ ruleLabel: 'Invalid JSON', detail: 'open Object fields to fix', severity: 'assert' }] }];
    }
  }

  // Steps into the row's sub-template in place by pushing the current level onto the stack, reusing this same view rather than a second dialog.
  // Nested array-object fields work the same way, one stack frame per level.
  configureSubTemplate(row: TemplateRow): void {
    this.stack.push({
      jsonText: this.jsonText,
      rows: this.rows,
      viewMode: this.viewMode,
      nextGroupId: this.nextGroupId,
      parentSubContext: this.currentSubContext,
      editingRow: row,
    });
    this.currentSubContext = {
      fieldPath: row.path || '(unnamed field)',
      rangeLabel: (row.loopStart || '0') + '–' + (row.loopEnd || '0'),
    };
    this.jsonText = row.subTemplate || '{\n\n}';
    this.viewMode = 'table';
    this.jsonError = '';
    this.rebuildRowsFromJson();
  }

  // Blank Start/End means "check every element up to the max" (0..MAX_LOOP_RANGE-1), not a single-element range. Shared by rowToInnerValue and loopRangeInvalid.
  private effectiveLoopBounds(row: TemplateRow): { start: number; end: number } {
    const hasStart = row.loopStart !== undefined && row.loopStart !== null && row.loopStart !== '';
    const hasEnd = row.loopEnd !== undefined && row.loopEnd !== null && row.loopEnd !== '';
    return {
      start: hasStart ? Number(row.loopStart) : 0,
      end: hasEnd ? Number(row.loopEnd) : MAX_LOOP_RANGE - 1,
    };
  }

  // Inclusive range (size = end - start + 1) must stay within MAX_LOOP_RANGE and end can't precede start.
  loopRangeInvalid(row: TemplateRow): boolean {
    if (row.ruleType !== 'array_object') {
      return false;
    }
    const { start, end } = this.effectiveLoopBounds(row);
    if (isNaN(start) || isNaN(end) || end < start) {
      return true;
    }
    return end - start + 1 > MAX_LOOP_RANGE;
  }

  hasInvalidLoopRange(): boolean {
    return this.rows.some((row) => this.loopRangeInvalid(row));
  }

  // ===== Dialog actions =====

  // Validates and serializes the current level; returns null (with jsonError set) on failure so callers stay put. Shared by save() and goBackClicked().
  private serializeCurrentLevel(beforeAction: string): string | null {
    if (this.viewMode === 'table') {
      if (this.hasInvalidLoopRange()) {
        this.jsonError = `One or more "Array object" checks has an invalid range -- end must not be before start, and the range can cover at most ${MAX_LOOP_RANGE} indices.`;
        return null;
      }
      return JSON.stringify(this.buildJsonFromRows(), null, 2);
    }
    if (!this.rebuildRowsFromJson(beforeAction)) {
      return null; // invalid JSON — stay here so the user can fix it
    }
    return this.jsonText;
  }

  private popStack(): void {
    const frame = this.stack.pop();
    if (!frame) {
      return;
    }
    this.jsonText = frame.jsonText;
    this.rows = frame.rows;
    this.viewMode = frame.viewMode;
    this.nextGroupId = frame.nextGroupId;
    this.currentSubContext = frame.parentSubContext;
    this.jsonError = '';
  }

  // Commits the current sub-template level's edits into the row that opened it, then steps up.
  goBackClicked(): void {
    if (this.stack.length === 0) {
      return;
    }
    const serialized = this.serializeCurrentLevel('going back');
    if (serialized === null) {
      return;
    }
    this.stack[this.stack.length - 1].editingRow.subTemplate = serialized;
    this.popStack();
  }

  // At root, closes the dialog with the final JSON; at a nested level, behaves like Back (commits into the parent row and steps up).
  save(): void {
    const serialized = this.serializeCurrentLevel(this.stack.length > 0 ? 'applying these changes' : 'saving');
    if (serialized === null) {
      return;
    }
    if (this.stack.length > 0) {
      this.stack[this.stack.length - 1].editingRow.subTemplate = serialized;
      this.popStack();
      return;
    }
    this.dialogRef.close(serialized);
  }

  // Abandons the whole editing session at every stack level, discarding all edits; Back/Save are for stepping up without discarding.
  cancel(): void {
    this.dialogRef.close(null);
  }
}
